import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { isMfaChallengeResponse } from '@keboola/api-client/developerPortal';
import type { LoginChallenge } from '@keboola/api-client/developerPortal/types';
import { Button, Card, setSubmitError, useAppForm } from '@keboola/design';

import { api, session } from '../../api';
import { useAuth } from '../../auth/context';
import { TextLink } from '../../components/TextLink';
import { getApiErrorMessage } from '../../features/auth/errors';
import { loginFormSchema, MFA_CODE_LENGTH, mfaFormSchema } from '../../features/auth/schemas';
import { formValidators } from '../../features/auth/validate';

/**
 * Whether `redirect` is a same-origin relative path — and nothing else.
 *
 * - Guards `navigate()` against open redirects via the `/~` and `/admin` guards' stashed URL.
 * - Parses against a fixed base rather than pattern-matching the string.
 * - Rejects `//evil.example` and `/\evil.example` (URL parsing turns `\` into `/`).
 */
const isSameOriginPath = (value: string): boolean => {
  if (!value.startsWith('/')) return false;
  try {
    const base = 'https://developer-portal.invalid';
    return new URL(value, base).origin === base;
  } catch {
    return false;
  }
};

/** Exported for `login.test.ts`; handed to `validateSearch` below. */
export const searchSchema = z.object({
  // `catch` keeps a mangled URL from throwing on the page that recovers a broken session.
  redirect: z.string().refine(isSameOriginPath).optional().catch(undefined),
});

export const Route = createFileRoute('/auth/login')({
  validateSearch: searchSchema,
  component: LoginPage,
  head: () => ({ meta: [{ title: 'Sign in | Keboola Developer Portal' }] }),
});

/** The MFA challenge apps-api handed back, carried between the two phases of the sign-in. */
type Challenge = {
  email: string;
  session: string;
  challenge?: LoginChallenge['challenge'];
};

function LoginPage() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [challenge, setChallenge] = useState<Challenge | null>(null);

  const finish = async () => {
    // `redirect` is schema-constrained to a relative path above.
    await navigate({ to: redirect ?? '/~' });
  };

  if (isAuthenticated) {
    // Not a redirect: an expired-but-present session lands here, so bouncing back would loop.
    return (
      <div className="tw:flex tw:flex-col tw:gap-4">
        <Card.Title as="h1">You are signed in</Card.Title>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          Continue to your vendor dashboard, or sign out from there to use a different account.
        </p>
        <Button asChild variant="primary">
          <Link to="/~">Go to the dashboard</Link>
        </Button>
      </div>
    );
  }

  return challenge === null ? (
    <PasswordPhase onChallenge={setChallenge} onSignedIn={finish} />
  ) : (
    <MfaPhase challenge={challenge} onCancel={() => setChallenge(null)} onSignedIn={finish} />
  );
}

function PasswordPhase({
  onChallenge,
  onSignedIn,
}: {
  onChallenge: (challenge: Challenge) => void;
  onSignedIn: () => Promise<void>;
}) {
  const form = useAppForm({
    defaultValues: { email: '', password: '' },
    validators: formValidators(loginFormSchema),
    // A failed sign-in marks the form invalid; without this, submit stays disabled and blocks retry.
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        const result = await api.auth.login(value);
        // An MFA challenge is a **200**, not an error — branch on the shape, not on catch.
        if (isMfaChallengeResponse(result)) {
          onChallenge({ email: value.email, session: result.session, challenge: result.challenge });
          return;
        }
        session.set(result);
        await onSignedIn();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'There was a problem signing you in.'));
      }
    },
  });

  return (
    <form.AppForm>
      <form.FormBody>
        <Card.Title as="h1">Sign in</Card.Title>
        {/* Repeated from sign-up: users reach for their Keboola password here. */}
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          A Developer Portal account is separate from your Keboola project login.
        </p>

        <form.AppField name="email">
          {(field) => (
            <field.TextField
              label="Email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          )}
        </form.AppField>

        <form.AppField name="password">
          {(field) => (
            <field.PasswordField
              label="Password"
              required
              autoComplete="current-password"
              allowPasswordToggle
            />
          )}
        </form.AppField>

        {/* Beside the password field, apart from the sign-up link. `-mt-2` offsets `FormBody`'s
            `gap-4` so it attaches to the field. */}
        <div className="tw:-mt-2 tw:text-sm">
          <TextLink to="/auth/reset-password">Forgot your password?</TextLink>
        </div>

        <form.SubmitErrorAlert />
        <form.SubmitButton>Sign in</form.SubmitButton>

        <p className="tw:m-0 tw:border-t tw:border-border tw:pt-4 tw:text-sm tw:text-muted-foreground">
          Don&apos;t have an account? <TextLink to="/auth/create-account">Create one</TextLink>
        </p>
      </form.FormBody>
    </form.AppForm>
  );
}

function MfaPhase({
  challenge,
  onCancel,
  onSignedIn,
}: {
  challenge: Challenge;
  onCancel: () => void;
  onSignedIn: () => Promise<void>;
}) {
  const form = useAppForm({
    defaultValues: { code: '' },
    validators: formValidators(mfaFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        // Same `POST /auth/login` — apps-api has no separate MFA endpoint.
        const result = await api.auth.login({
          email: challenge.email,
          session: challenge.session,
          code: value.code,
          // Echoed back only when apps-api sent one — it may omit `challenge` and still expect the code.
          ...(challenge.challenge === undefined ? {} : { challenge: challenge.challenge }),
        });
        if (isMfaChallengeResponse(result)) {
          // Neutral wording: the same form takes a texted code.
          setSubmitError(formApi, 'That code was not accepted. Check it and try again.');
          return;
        }
        session.set(result);
        await onSignedIn();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'That code was not accepted.'));
      }
    },
  });

  return (
    <form.AppForm>
      <form.FormBody>
        <Card.Title as="h1">Two-factor authentication</Card.Title>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          {challenge.challenge === 'SMS_MFA'
            ? 'Enter the code we sent you by text message.'
            : 'Enter the code from your authenticator app.'}
        </p>

        <form.AppField name="code">
          {(field) => (
            <field.OtpInputField label="Verification code" required maxLength={MFA_CODE_LENGTH} />
          )}
        </form.AppField>

        <form.SubmitErrorAlert />
        <form.SubmitButton>Verify</form.SubmitButton>

        <Button variant="invisible" onClick={onCancel}>
          Use a different account
        </Button>
      </form.FormBody>
    </form.AppForm>
  );
}
